/* theme.js - Main JavaScript for Pavement Roast B2B Theme */

// Debounce utility for performance optimization
function debounce(func, wait = 150) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Throttle utility for scroll and resize events
function throttle(func, limit = 100) {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Mobile Menu Toggle
class MobileMenuToggle {
  constructor() {
    this.menuToggle = document.querySelector('.header__menu-toggle');
    this.nav = document.querySelector('.header__nav');
    this.init();
  }

  init() {
    if (!this.menuToggle) return;
    this.menuToggle.addEventListener('click', () => this.toggle());
  }

  toggle() {
    this.menuToggle.classList.toggle('active');
    if (this.nav) {
      this.nav.style.display = this.nav.style.display === 'flex' ? 'none' : 'flex';
    }
  }
}

// Announcement Bar Dismiss
class AnnouncementBar {
  constructor() {
    this.bar = document.querySelector('.announcement-bar');
    this.closeBtn = document.querySelector('.announcement-bar__close');
    this.storageKey = 'announcement-bar-dismissed';
    this.init();
  }

  init() {
    if (!this.bar) return;

    if (this.isDismissed()) {
      this.bar.style.display = 'none';
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.dismiss());
    }
  }

  dismiss() {
    this.bar.style.display = 'none';
    localStorage.setItem(this.storageKey, 'true');
  }

  isDismissed() {
    return localStorage.getItem(this.storageKey) === 'true';
  }
}

// Quantity Selector Component
class QuantitySelector {
  constructor(container) {
    this.container = container;
    this.decrementBtn = container.querySelector('button[data-action="decrease"]');
    this.incrementBtn = container.querySelector('button[data-action="increase"]');
    this.input = container.querySelector('input[type="number"]');
    this.minQuantity = parseInt(container.dataset.min || 1, 10);
    this.maxQuantity = parseInt(container.dataset.max || 999, 10);
    this.init();
  }

  init() {
    if (!this.input) return;
    this.decrementBtn?.addEventListener('click', () => this.decrement());
    this.incrementBtn?.addEventListener('click', () => this.increment());
    this.input.addEventListener('change', () => this.validate());
  }

  increment() {
    const current = parseInt(this.input.value, 10);
    if (current < this.maxQuantity) {
      this.input.value = current + 1;
      this.updateButtonStates();
      this.triggerChange();
    }
  }

  decrement() {
    const current = parseInt(this.input.value, 10);
    if (current > this.minQuantity) {
      this.input.value = current - 1;
      this.updateButtonStates();
      this.triggerChange();
    }
  }

  validate() {
    let value = parseInt(this.input.value, 10);
    if (isNaN(value) || value < this.minQuantity) {
      value = this.minQuantity;
    }
    if (value > this.maxQuantity) {
      value = this.maxQuantity;
    }
    this.input.value = value;
    this.updateButtonStates();
  }

  updateButtonStates() {
    const current = parseInt(this.input.value, 10);
    this.decrementBtn.disabled = current <= this.minQuantity;
    this.incrementBtn.disabled = current >= this.maxQuantity;
  }

  triggerChange() {
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

// Smooth Scroll for Anchor Links
class SmoothScroll {
  constructor() {
    this.init();
  }

  init() {
    document.querySelectorAll('a[href^="#"]').forEach((link) => {
      link.addEventListener('click', (e) => this.handleClick(e, link));
    });
  }

  handleClick(e, link) {
    const href = link.getAttribute('href');
    if (href === '#' || href === '') return;

    const target = document.querySelector(href);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth' });
      target.focus();
    }
  }
}

// Intersection Observer for Scroll Animations
class ScrollAnimations {
  constructor() {
    this.init();
  }

  init() {
    const options = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, options);

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });
  }
}

// Accordion Component for FAQs
class Accordion {
  constructor(container) {
    this.container = container;
    this.items = container.querySelectorAll('[data-accordion-item]');
    this.init();
  }

  init() {
    this.items.forEach((item) => {
      const trigger = item.querySelector('[data-accordion-trigger]');
      if (trigger) {
        trigger.addEventListener('click', () => this.toggle(item));
      }
    });
  }

  toggle(item) {
    const isOpen = item.classList.contains('active');

    this.items.forEach((i) => {
      i.classList.remove('active');
    });

    if (!isOpen) {
      item.classList.add('active');
    }
  }
}

// Form Validation for Wholesale Inquiry
class FormValidator {
  constructor(form) {
    this.form = form;
    this.fields = form.querySelectorAll('[data-validate]');
    this.init();
  }

  init() {
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    this.fields.forEach((field) => {
      field.addEventListener('blur', () => this.validateField(field));
      field.addEventListener('change', () => this.validateField(field));
    });
  }

  handleSubmit(e) {
    let isValid = true;
    this.fields.forEach((field) => {
      if (!this.validateField(field)) {
        isValid = false;
      }
    });

    if (!isValid) {
      e.preventDefault();
    }
  }

  validateField(field) {
    const value = field.value.trim();
    const type = field.dataset.validate;
    let isValid = true;

    // Remove previous error states
    field.classList.remove('error');
    const errorMsg = field.parentElement.querySelector('.form-error');
    if (errorMsg) {
      errorMsg.remove();
    }

    // Validation rules
    if (type === 'required' && !value) {
      isValid = false;
      this.showError(field, 'This field is required');
    } else if (type === 'email' && value && !this.isValidEmail(value)) {
      isValid = false;
      this.showError(field, 'Please enter a valid email');
    } else if (type === 'phone' && value && !this.isValidPhone(value)) {
      isValid = false;
      this.showError(field, 'Please enter a valid phone number');
    } else if (type === 'number' && value && isNaN(value)) {
      isValid = false;
      this.showError(field, 'Please enter a valid number');
    }

    if (isValid) {
      field.classList.remove('error');
    }

    return isValid;
  }

  isValidEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  isValidPhone(phone) {
    const re = /^[\d+\-\s()]+$/;
    return re.test(phone) && phone.replace(/\D/g, '').length >= 10;
  }

  showError(field, message) {
    field.classList.add('error');
    const errorDiv = document.createElement('div');
    errorDiv.className = 'form-error';
    errorDiv.textContent = message;
    field.parentElement.appendChild(errorDiv);
  }
}

// Cart/AJAX Add to Cart
class CartManager {
  constructor() {
    this.cartDrawer = document.querySelector('[data-cart-drawer]');
    this.cartToggle = document.querySelector('[data-cart-toggle]');
    this.addToCartBtns = document.querySelectorAll('[data-add-to-cart]');
    this.init();
  }

  init() {
    this.cartToggle?.addEventListener('click', () => this.toggleDrawer());
    this.addToCartBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => this.addToCart(e));
    });
  }

  toggleDrawer() {
    if (this.cartDrawer) {
      this.cartDrawer.classList.toggle('active');
    }
  }

  addToCart(e) {
    e.preventDefault();
    const button = e.currentTarget;
    const form = button.closest('form');

    if (!form) {
      console.error('No form found for add to cart');
      return;
    }

    const formData = new FormData(form);
    this.showLoadingState(button);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams(formData)
    })
      .then((response) => response.json())
      .then((data) => {
        this.handleAddToCartSuccess(button, data);
      })
      .catch((error) => {
        console.error('Error:', error);
        this.showError(button, 'Failed to add to cart');
      })
      .finally(() => {
        this.hideLoadingState(button);
      });
  }

  handleAddToCartSuccess(button, data) {
    this.updateCartCount();
    this.showSuccess(button, 'Added to cart');
    this.toggleDrawer();
  }

  updateCartCount() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((data) => {
        const cartCount = document.querySelector('[data-cart-count]');
        if (cartCount) {
          cartCount.textContent = data.item_count;
          cartCount.classList.add('updated');
        }
        // Update header cart badge
        const badges = document.querySelectorAll('.header__cart-badge');
        if (data.item_count > 0) {
          badges.forEach(b => { b.textContent = data.item_count; b.style.display = 'flex'; });
          if (badges.length === 0) {
            const cartLink = document.querySelector('.header__utility-icon--cart');
            if (cartLink) {
              const badge = document.createElement('span');
              badge.className = 'header__cart-badge';
              badge.setAttribute('aria-label', data.item_count + ' items in cart');
              badge.textContent = data.item_count;
              cartLink.appendChild(badge);
            }
          }
        } else {
          badges.forEach(b => b.style.display = 'none');
        }
      });
  }

  showLoadingState(button) {
    button.disabled = true;
    button.dataset.originalText = button.textContent;
    button.textContent = 'Adding...';
  }

  hideLoadingState(button) {
    button.disabled = false;
    button.textContent = button.dataset.originalText || 'Add to Cart';
  }

  showSuccess(button, message) {
    const originalText = button.textContent;
    button.textContent = message;
    button.style.backgroundColor = '#4caf50';
    setTimeout(() => {
      button.textContent = originalText;
      button.style.backgroundColor = '';
    }, 2000);
  }

  showError(button, message) {
    const originalText = button.textContent;
    button.textContent = message;
    button.style.backgroundColor = '#d32f2f';
    setTimeout(() => {
      button.textContent = originalText;
      button.style.backgroundColor = '';
    }, 2000);
  }
}

// Predictive Search
class PredictiveSearch {
  constructor() {
    this.searchInput = document.querySelector('[data-search-input]');
    this.searchResults = document.querySelector('[data-search-results]');
    this.init();
  }

  init() {
    if (!this.searchInput) return;

    this.searchInput.addEventListener(
      'input',
      debounce((e) => this.handleSearch(e), 300)
    );

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-search-input]')) {
        this.closeResults();
      }
    });
  }

  handleSearch(e) {
    const query = e.target.value.trim();

    if (query.length < 2) {
      this.closeResults();
      return;
    }

    fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product`)
      .then((response) => response.json())
      .then((data) => this.displayResults(data))
      .catch((error) => console.error('Search error:', error));
  }

  displayResults(data) {
    if (!this.searchResults || !data.resources.results.products) return;

    const products = data.resources.results.products.slice(0, 5);

    if (products.length === 0) {
      this.searchResults.innerHTML = '<p>No results found</p>';
      this.searchResults.style.display = 'block';
      return;
    }

    const html = products
      .map(
        (product) => `
      <a href="${product.url}" class="search-result-item">
        <div class="search-result-title">${product.title}</div>
      </a>
    `
      )
      .join('');

    this.searchResults.innerHTML = html;
    this.searchResults.style.display = 'block';
  }

  closeResults() {
    if (this.searchResults) {
      this.searchResults.style.display = 'none';
    }
  }
}

// Lazy Loading Images
class LazyLoad {
  constructor() {
    this.init();
  }

  init() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.classList.add('loaded');
            observer.unobserve(img);
          }
        });
      });

      document.querySelectorAll('img[data-src]').forEach((img) => {
        observer.observe(img);
      });
    }
  }
}

// Quick Add Modal
class QuickAddModal {
  constructor() {
    this.modal = document.getElementById('quick-add-modal');
    this.body = document.getElementById('quick-add-modal-body');
    this.backdrop = this.modal?.querySelector('.quick-add-modal__backdrop');
    this.closeBtn = this.modal?.querySelector('.quick-add-modal__close');
    this.init();
  }

  init() {
    if (!this.modal) return;

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (btn) {
        e.preventDefault();
        this.open(btn.dataset.productUrl);
      }
    });

    this.backdrop?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.getAttribute('aria-hidden') === 'false') {
        this.close();
      }
    });
  }

  async open(productUrl) {
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.body.innerHTML = '<div class="quick-add-modal__loading"><div class="quick-add-modal__spinner"></div></div>';

    try {
      const response = await fetch(productUrl);
      const product = await response.json();
      this.render(product);
    } catch (err) {
      this.body.innerHTML = '<p style="text-align:center;padding:40px;">Failed to load product. Please try again.</p>';
      console.error('Failed to fetch product:', err);
    }
  }

  close() {
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.body.innerHTML = '';
  }

  render(product) {
    const variant = product.variants[0];
    const hasMultipleVariants = product.variants.length > 1;
    const imgSrc = product.featured_image ? product.featured_image.replace(/(\.[^.]+)$/, '_400x$1') : '';

    let optionsHtml = '';
    if (hasMultipleVariants) {
      product.options.forEach((option, idx) => {
        const optionName = (typeof option === 'string') ? option : (option.name || option.label || 'Option ' + (idx + 1));
        const values = [...new Set(product.variants.map(v => v.options[idx]))];
        optionsHtml += '<div class="quick-add-modal__option">' +
          '<label class="quick-add-modal__label">' + this.escapeHtml(optionName) + '</label>' +
          '<select class="quick-add-modal__select" data-option-index="' + idx + '">' +
          values.map(v => '<option value="' + this.escapeHtml(v) + '">' + this.escapeHtml(v) + '</option>').join('') +
          '</select></div>';
      });
    }

    this.body.innerHTML =
      '<div class="quick-add-modal__product">' +
        (imgSrc ? '<div class="quick-add-modal__image"><img src="' + imgSrc + '" alt="' + this.escapeHtml(product.title) + '" id="quick-add-image"></div>' : '') +
        '<div class="quick-add-modal__info">' +
          (product.vendor ? '<p class="quick-add-modal__vendor">' + this.escapeHtml(product.vendor) + '</p>' : '') +
          '<h3 class="quick-add-modal__title">' + this.escapeHtml(product.title) + '</h3>' +
          '<div class="quick-add-modal__price" id="quick-add-price">' +
            this.formatPrice(variant.price) +
          '</div>' +
          optionsHtml +
          ((window.PavementRoast?.b2b?.moq || 1) > 1 ?
            '<div class="quick-add-modal__moq-notice">' +
              '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10.5a.75.75 0 110-1.5.75.75 0 010 1.5zM8.75 7.5a.75.75 0 01-1.5 0v-3a.75.75 0 011.5 0v3z" fill="currentColor"/></svg>' +
              'Minimum order: <strong>' + (window.PavementRoast?.b2b?.moq || 1) + ' units</strong>' +
            '</div>' : '') +
          '<div class="quick-add-modal__quantity">' +
            '<label class="quick-add-modal__label">Quantity</label>' +
            '<div class="quick-add-modal__qty-wrap">' +
              '<button type="button" class="quick-add-modal__qty-btn" data-qa-qty="minus" aria-label="Decrease quantity">-</button>' +
              '<input type="number" value="' + (window.PavementRoast?.b2b?.moq || 1) + '" min="' + (window.PavementRoast?.b2b?.moq || 1) + '" class="quick-add-modal__qty-input" id="quick-add-qty" aria-label="Quantity">' +
              '<button type="button" class="quick-add-modal__qty-btn" data-qa-qty="plus" aria-label="Increase quantity">+</button>' +
            '</div>' +
          '</div>' +
          '<button type="button" class="quick-add-modal__add-btn" id="quick-add-submit" data-variant-id="' + variant.id + '">' +
            'Add to Cart' +
          '</button>' +
        '</div>' +
      '</div>';

    this.bindEvents(product);
  }

  bindEvents(product) {
    const selects = this.body.querySelectorAll('.quick-add-modal__select');
    const addBtn = this.body.querySelector('#quick-add-submit');
    const priceEl = this.body.querySelector('#quick-add-price');
    const imageEl = this.body.querySelector('#quick-add-image');
    const qtyInput = this.body.querySelector('#quick-add-qty');
    const minusBtn = this.body.querySelector('[data-qa-qty="minus"]');
    const plusBtn = this.body.querySelector('[data-qa-qty="plus"]');

    minusBtn?.addEventListener('click', () => {
      const moq = window.PavementRoast?.b2b?.moq || 1;
      const val = parseInt(qtyInput.value);
      if (val > moq) qtyInput.value = val - 1;
    });

    plusBtn?.addEventListener('click', () => {
      qtyInput.value = parseInt(qtyInput.value) + 1;
    });

    const updateVariant = () => {
      const options = Array.from(selects).map(s => s.value);
      const variant = product.variants.find(v =>
        v.options.every((opt, idx) => options[idx] === undefined || opt === options[idx])
      );
      if (variant) {
        addBtn.dataset.variantId = variant.id;
        priceEl.textContent = this.formatPrice(variant.price);
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
        if (variant.featured_image && imageEl) {
          imageEl.src = variant.featured_image.src.replace(/(\.[^.]+)$/, '_400x$1');
        }
      }
    };

    selects.forEach(s => s.addEventListener('change', updateVariant));

    addBtn.addEventListener('click', async () => {
      const variantId = addBtn.dataset.variantId;
      const qty = parseInt(qtyInput.value) || 1;
      const origText = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding...';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId), quantity: qty })
        });

        if (!response.ok) throw new Error('Network response was not ok');

        const data = await response.json();
        addBtn.textContent = 'Added!';
        addBtn.style.backgroundColor = '#2d7d2d';
        setTimeout(() => {
          this.close();
        }, 1000);
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      } catch (err) {
        addBtn.textContent = 'Error';
        addBtn.style.backgroundColor = '#d32f2f';
        setTimeout(() => {
          addBtn.textContent = origText;
          addBtn.style.backgroundColor = '';
          addBtn.disabled = false;
        }, 1500);
        console.error('Add to cart error:', err);
      }
    });
  }

  formatPrice(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize all components when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new MobileMenuToggle();
  new AnnouncementBar();
  new SmoothScroll();
  new ScrollAnimations();
  new CartManager();
  new PredictiveSearch();
  new LazyLoad();
  new QuickAddModal();

  // Update cart badge on page load (handles Shopify page caching)
  fetch('/cart.js')
    .then(r => r.json())
    .then(cart => {
      const badges = document.querySelectorAll('.header__cart-badge');
      if (cart.item_count > 0) {
        badges.forEach(b => { b.textContent = cart.item_count; b.style.display = 'flex'; });
        if (badges.length === 0) {
          const cartLink = document.querySelector('.header__utility-icon--cart');
          if (cartLink) {
            const badge = document.createElement('span');
            badge.className = 'header__cart-badge';
            badge.setAttribute('aria-label', cart.item_count + ' items in cart');
            badge.textContent = cart.item_count;
            cartLink.appendChild(badge);
          }
        }
      } else {
        badges.forEach(b => b.style.display = 'none');
      }
    })
    .catch(() => {});

  // Initialize quantity selectors
  document.querySelectorAll('[data-quantity-selector]').forEach((el) => {
    new QuantitySelector(el);
  });

  // Initialize accordions
  document.querySelectorAll('[data-accordion]').forEach((el) => {
    new Accordion(el);
  });

  // Initialize form validators
  document.querySelectorAll('[data-form-validate]').forEach((el) => {
    new FormValidator(el);
  });
});

// Export for external use
window.Shopify = window.Shopify || {};
window.Shopify.theme = {
  CartManager,
  FormValidator,
  QuantitySelector,
  Accordion,
  QuickAddModal,
  debounce,
  throttle
};
